import { computed, inject, Injectable } from '@angular/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { lastValueFrom, map } from 'rxjs';

import { StudentService } from '@/api/services';
import { StudentsScreenAdvancedFilters } from '@/screens/students/students.component';
import { AdvancedFiltersService } from '@/shared/services/advanced-filters.service';
import { FilterConfig, FilterType, ListService } from '@/shared/services/list.service';
import { toInfiniteQuery } from '@/shared/utils/infinitequery';

@Injectable({
  providedIn: 'any'
})
export class StudentsListService extends ListService {
  private apiService = inject(StudentService);
  private advancedFiltersService = inject(AdvancedFiltersService<StudentsScreenAdvancedFilters>);

  private readonly filterConfig: FilterConfig = {
    is_online: {
      value: `{"field":"student.is_online","operator":"equal","value":true}`,
      type: FilterType.AND
    },
    is_subscribed: {
      value: `{"field":"student.subscribed","operator":"equal","value":true}`,
      type: FilterType.AND
    },
    is_business_school: {
      value: `{"field":"student.is_business_school","operator":"equal","value":true}`,
      type: FilterType.AND
    },
    is_high_education: {
      value: `{"field":"student.is_high_education","operator":"equal","value":true}`,
      type: FilterType.AND
    },
    has_teaching_tickets: {
      value: `{"field":"student.has_teaching_tickets","operator":"equal","value":true}`,
      type: FilterType.AND
    }
  };

  searchArray = computed(() => {
    if (!this.search()) return [];

    return [
      `{"field":"student.full_name", "operator":"contains_ai_ci", "value":"${this.search()}"}`,
      `{"field":"student.email", "operator":"contains_ai_ci", "value":"${this.search()}"}`
    ];
  });

  readonly query = injectQuery(() => ({
    queryKey: [
      'students',
      this.pageParam(),
      this.limit(),
      this.sortingString(),
      this.searchArray(),
      this.filtersString(),
      this.advancedFiltersService.advancedFilters()
    ] as const,
    enabled: this.isQueryActive(),
    queryFn: async ({ queryKey }) => {
      const page = queryKey[1];
      const limit = queryKey[2];
      const sort = queryKey[3];
      const advancedFilters = queryKey[6] as StudentsScreenAdvancedFilters;

      // Add dynamic advanced filters
      const dynamicFilters = this.createDynamicFilters(advancedFilters);

      const filters = this.buildFilters(this.searchArray(), queryKey[5] as string, dynamicFilters, advancedFilters);

      const params = this.getQueryParams({ page, limit, sort, filters });

      return lastValueFrom(this.apiService.apiStudentOverviewGet(params).pipe(toInfiniteQuery(params)));
    }
  }));

  readonly queryCount = injectQuery(() => ({
    queryKey: [
      'students-online-count',
      this.searchArray(),
      this.filtersString(),
      this.advancedFiltersService.advancedFilters()
    ],
    enabled: this.isQueryActive(),
    queryFn: async ({ queryKey }) => {
      const advancedFilters = queryKey[3] as StudentsScreenAdvancedFilters;

      // Add dynamic advanced filters
      const dynamicFilters = this.createDynamicFilters(advancedFilters);

      const filters = this.buildFilters(this.searchArray(), queryKey[2] as string, dynamicFilters, advancedFilters);

      const params = this.getQueryParams({ page: undefined, limit: undefined, sort: undefined, filters });

      return lastValueFrom(this.apiService.apiStudentOnlineGet(params).pipe(map((res) => res.count)));
    }
  }));

  readonly queryCountCounters = injectQuery(() => ({
    queryKey: [
      'students-counters-count',
      this.searchArray(),
      this.filtersString(),
      this.advancedFiltersService.advancedFilters()
    ],
    enabled: this.isQueryActive(),
    queryFn: async ({ queryKey }) => {
      const advancedFilters = queryKey[3] as StudentsScreenAdvancedFilters;

      // Add dynamic advanced filters
      const dynamicFilters = this.createDynamicFilters(advancedFilters);

      const filters = this.buildFilters(this.searchArray(), queryKey[2] as string, dynamicFilters, advancedFilters);

      const params = this.getQueryParams({ page: undefined, limit: undefined, sort: undefined, filters });

      return lastValueFrom(this.apiService.apiStudentCounterQuickFiltersGet(params).pipe(map((res) => res)));
    }
  }));

  private createDynamicFilters(advancedFilters: StudentsScreenAdvancedFilters): FilterConfig {
    const formatDateForRange = (date: Date | string | null, isEnd = false): string | null => {
      if (!date) return null;

      const actualDate = typeof date === 'string' ? new Date(date) : date;

      const year = actualDate.getFullYear();
      const month = String(actualDate.getMonth() + 1).padStart(2, '0');
      const day = String(actualDate.getDate()).padStart(2, '0');
      const hour = isEnd ? '23' : '00';
      const minute = isEnd ? '59' : '00';
      const second = isEnd ? '59' : '00';

      return `${year}-${month}-${day} ${hour}:${minute}:${second}+00`;
    };

    return {
      ...this.filterConfig,
      ...(advancedFilters.subscribed && {
        subscribed: {
          value: `{"field":"student.subscribed","operator":"equal","value":${JSON.stringify(advancedFilters.subscribed)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.university && {
        university: {
          value: `{"field":"student.university","operator":"equal","value":${JSON.stringify(advancedFilters.university)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.region && {
        region: {
          value: `{"field":"region.id","operator":"in","value":${JSON.stringify(advancedFilters.region)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.countries && {
        countries: {
          value: `{"field":"country.id","operator":"in","value":${JSON.stringify(advancedFilters.countries)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.educational_level && {
        educational_level: {
          value: `{"field":"educational_level.id","operator":"in","value":${JSON.stringify(advancedFilters.educational_level)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.category && {
        category: {
          value: `{"field":"category.id","operator":"in","value":${JSON.stringify(advancedFilters.category)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.enrolmentStatus && {
        enrolmentStatus: {
          value: `{"field":"enrolment.status","operator":"in","value":${JSON.stringify(advancedFilters.enrolmentStatus)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.institution && {
        institution: {
          value: `{"field":"institution.id","operator":"in","value":${JSON.stringify(advancedFilters.institution)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.double_institution && {
        double_institution: {
          value: `{"field":"institution.id","operator":"in","value":${JSON.stringify(advancedFilters.double_institution)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.area && {
        area: {
          value: `{"field":"area.id","operator":"in","value":${JSON.stringify(advancedFilters.area)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.faculty && {
        faculty: {
          value: `{"field":"faculty.id","operator":"in","value":${JSON.stringify(advancedFilters.faculty)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.school && {
        school: {
          value: `{"field":"school.id","operator":"in","value":${JSON.stringify(advancedFilters.school)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.group && {
        group: {
          value: `{"field":"group.id","operator":"in","value":${JSON.stringify(advancedFilters.group)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.course && {
        course: {
          value: `{"field":"course.id","operator":"in","value":${JSON.stringify(advancedFilters.course)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.merchant && {
        merchant: {
          value: `{"field":"merchant.id","operator":"in","value":${JSON.stringify(advancedFilters.merchant)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.enrolmentStartDate &&
        (advancedFilters.enrolmentStartDate.start || advancedFilters.enrolmentStartDate.end) && {
          enrolmentStartDate: {
            value: advancedFilters.enrolmentStartDate.end
              ? `{"field":"enrolment.start_date","operator":"between","value":[${JSON.stringify(formatDateForRange(advancedFilters.enrolmentStartDate.start))},${JSON.stringify(formatDateForRange(advancedFilters.enrolmentStartDate.end, true))}]}`
              : `{"field":"enrolment.start_date","operator":"between","value":[${JSON.stringify(formatDateForRange(advancedFilters.enrolmentStartDate.start))},${JSON.stringify(formatDateForRange(advancedFilters.enrolmentStartDate.start, true))}]}`,
            type: FilterType.AND
          }
        }),
      ...(advancedFilters.enrolmentEndDate &&
        (advancedFilters.enrolmentEndDate.start || advancedFilters.enrolmentEndDate.end) && {
          enrolmentEndDate: {
            value: advancedFilters.enrolmentEndDate.end
              ? `{"field":"enrolment.end_date","operator":"between","value":[${JSON.stringify(formatDateForRange(advancedFilters.enrolmentEndDate.start))},${JSON.stringify(formatDateForRange(advancedFilters.enrolmentEndDate.end, true))}]}`
              : `{"field":"enrolment.end_date","operator":"between","value":[${JSON.stringify(formatDateForRange(advancedFilters.enrolmentEndDate.start))},${JSON.stringify(formatDateForRange(advancedFilters.enrolmentEndDate.start, true))}]}`,
            type: FilterType.AND
          }
        })
    } as FilterConfig;
  }

  getStudentsCurrentFilters(): Record<string, unknown> {
    const page = this.pageParam();
    const sort = this.sortingString();
    const advancedFilters = this.advancedFiltersService.advancedFilters() as StudentsScreenAdvancedFilters;

    // Add dynamic advanced filters
    const dynamicFilters = this.createDynamicFilters(advancedFilters);

    const filters = this.buildFilters(
      this.searchArray(),
      this.filtersString() as string,
      dynamicFilters,
      advancedFilters
    );

    return this.getQueryParams({ page, sort, filters });
  }
}
